/**
 * Runtime configuration. Vite inlines these at build time, so each stage gets
 * its own bundle rather than the app guessing its environment at runtime.
 */
const APP_DOMAIN = import.meta.env.VITE_APP_DOMAIN ?? 'petservices.kineticflux.net';

/**
 * True when this page is served from a sitter's own hostname rather than from
 * the platform. The same bundle serves both, so this is a question about the
 * address bar, not about the build.
 */
export function isCustomDomain(): boolean {
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return false;
  return host !== APP_DOMAIN && !host.endsWith(`.${APP_DOMAIN}`);
}

/**
 * The platform's own host for this stage. Every non-prod stage is a label under
 * the app domain, so a dev build addresses dev.petservices... while the app
 * domain itself stays the bare one. The guard is for a build whose
 * VITE_APP_DOMAIN already carries the stage label: adding it again produced
 * dev.dev.petservices... in every tenant link.
 */
function stageHost(): string {
  if (config.stage === 'prod') return config.appDomain;
  const prefix = `${config.stage}.`;
  return config.appDomain.startsWith(prefix) ? config.appDomain : prefix + config.appDomain;
}

export const config = {
  /**
   * On a white-labelled domain the API is served from that same domain under
   * /api, by a cache behaviour on the sitter's own distribution. A client's
   * browser then never contacts a petservices hostname, and because it is the
   * same origin there is no preflight and no CORS allow-list to maintain.
   */
  apiBaseUrl: isCustomDomain() ? '/api' : import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000',
  appDomain: APP_DOMAIN,
  stage: import.meta.env.VITE_STAGE ?? 'dev',
  cognito: {
    userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID ?? '',
    clientId: import.meta.env.VITE_COGNITO_CLIENT_ID ?? '',
  },
};

/** Subdomains the platform keeps for itself; never a tenant slug. */
const RESERVED = new Set(['www', 'api', 'app', 'admin', 'dev', 'stg', 'prod', 'assets', 'cdn']);

/**
 * Which business is this page for?
 *
 * In production each sitter is a subdomain. Locally there are no subdomains, so
 * the slug comes from the /b/:slug route, a ?tenant= parameter, or the last
 * business this browser saw.
 *
 * That last fallback is local-only on purpose. On a real host the hostname is
 * the whole answer: the platform's own domain must stay the platform's, not
 * whichever sitter the browser happened to visit first.
 */
export function resolveTenantSlug(): string | null {
  const { hostname, pathname, search } = window.location;

  const fromPath = /^\/b\/([a-z0-9-]+)/.exec(pathname);
  if (fromPath) return fromPath[1];

  const fromQuery = new URLSearchParams(search).get('tenant');
  if (fromQuery) return fromQuery;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return localStorage.getItem('petservices.tenantSlug');
  }

  const label = hostname.split('.')[0];
  if (!label || RESERVED.has(label) || !hostname.includes('.')) return null;
  return label;
}

export function rememberTenantSlug(slug: string): void {
  localStorage.setItem('petservices.tenantSlug', slug);
}

/**
 * The address a sitter hands to a client: their own subdomain of the platform.
 * Locally there are no subdomains, so the /b/:slug route stands in for one.
 *
 * A sitter on their own domain would not use this, which is why the caller
 * decides whether to show it rather than this function guessing.
 */
export function tenantSiteUrl(slug: string): string {
  const { hostname, origin } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return `${origin}/b/${slug}`;

  return `https://${slug}.${stageHost()}`;
}

/**
 * Where a signed-in person's own business lives.
 *
 * Tenancy is resolved from the hostname, and the platform's own host belongs
 * to no sitter. So signing in there leaves you with no tenant in context and
 * every role-guarded route bounces you back to the marketing page, which reads
 * exactly like being logged out. Anything that signs someone in on the
 * platform host has to send them somewhere that resolves to their business.
 *
 * Prefers a business they run over one they are merely a client of, since an
 * owner signing in is almost always going to their own admin.
 */
export function homeMembership<T extends { role: string; slug: string }>(
  memberships: T[],
): T | null {
  if (memberships.length === 0) return null;
  return memberships.find((m) => m.role === 'owner' || m.role === 'staff') ?? memberships[0];
}

/**
 * The full address of the screen a membership should actually open.
 *
 * The root of a business's host is its storefront, which is the page a
 * prospective CLIENT sees. Sending a sitter there lands them on "start my
 * pet's file", which reads as having been signed in as the wrong person.
 */
export function membershipHomeUrl(membership: { role: string; slug: string }): string {
  const path = membership.role === 'client' ? '/portal' : '/admin';
  return `${tenantSiteUrl(membership.slug)}${path}`;
}

/**
 * Leave for the screen a membership should open, from a page where no business
 * is in context.
 *
 * On a real host that is another origin, so it is a plain navigation. Locally
 * there are no subdomains and the guarded routes are unprefixed, so the slug is
 * written where `resolveTenantSlug` will find it and the same origin is
 * reloaded: a route change would not do, because the tenant is resolved once,
 * when the session loads, and a guarded route with no tenant bounces straight
 * back here.
 *
 * It replaces rather than pushes. Whatever it is leaving is a page this person
 * should not have been on, and leaving it in history means Back returns to it.
 */
export function goToMembershipHome(membership: { role: string; slug: string }): void {
  const { hostname, origin } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    rememberTenantSlug(membership.slug);
    window.location.replace(`${origin}${membership.role === 'client' ? '/portal' : '/admin'}`);
    return;
  }
  window.location.replace(membershipHomeUrl(membership));
}

/** Prefix for in-app links, so /b/:slug routing keeps working locally. */
export function tenantPathPrefix(): string {
  const fromPath = /^\/b\/([a-z0-9-]+)/.exec(window.location.pathname);
  return fromPath ? `/b/${fromPath[1]}` : '';
}

/** The platform's own address for this stage, for copy that has to show it. */
export function platformHost(): string {
  return stageHost();
}
